import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../../pages/CSS/marketplace-messaging.css';
import {
  useConversationMessages,
  useSendConversationMessage,
} from '../../hooks/useMarketplaceChat';
import { useBidActions } from '../../hooks/useBids';
import { useAuthStore } from '../../stores/authStore';
import { isOwnStoreConversation } from '../../utils/helpers';
import {
  getOfferFromMessage,
  isBuyerMessage,
  isOfferActiveAccepted,
  isOfferMessage,
  isAcceptedMessage,
} from '../../utils/offerNormalize';
import OfferMessageCard from '../marketplace/OfferMessageCard';
import ChatComposer from '../marketplace/ChatComposer';
import ChatSkeleton from '../marketplace/ChatSkeleton';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll';

const formatMsgTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const MarketplaceChatPane = ({
  conversationId,
  focusOffer = false,
  onBack,
  onConversationUpdate,
}) => {
  const { t } = useTranslation(['conversations', 'bids']);
  const user = useAuthStore((state) => state.user);
  const buyerId = user?.id || user?._id;

  const [chatText, setChatText] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [error, setError] = useState('');
  const [actionOfferId, setActionOfferId] = useState(null);
  const messagesContainerRef = useRef(null);
  const offerInputRef = useRef(null);
  const didFocusOfferRef = useRef(false);

  const {
    data: thread = { messages: [], conversation: null },
    isLoading,
    isFetching,
    refetch,
  } = useConversationMessages(conversationId, {
    enablePolling: !!conversationId,
    pollingInterval: 5000,
  });

  const conversation = thread.conversation;
  const messages = thread.messages ?? [];
  const storeName =
    conversation?.seller?.name ||
    conversation?.seller?.storeName ||
    t('officialStore');
  const isOwnThread = isOwnStoreConversation(conversation, user);

  const { mutateAsync: sendMessage, isPending: isSending } = useSendConversationMessage();
  const {
    placeBidAsync,
    isPlacingBid,
    addBidToCartAsync,
    isAddingBidToCart,
    acceptOfferAsync,
    rejectOfferAsync,
  } = useBidActions();

  const hasActiveAccepted = useMemo(() => {
    const accepted = messages.filter(isAcceptedMessage);
    const last = accepted.length > 0 ? accepted[accepted.length - 1] : null;
    return isOfferActiveAccepted(getOfferFromMessage(last));
  }, [messages]);

  const { scrollToBottom } = useChatAutoScroll({
    containerRef: messagesContainerRef,
    conversationKey: conversationId,
    messageCount: messages.length,
    enabled: !!conversationId && !isLoading,
  });

  useEffect(() => {
    const productId =
      conversation?.contextProduct?.id ||
      conversation?.contextProduct?._id;
    if (
      focusOffer &&
      !didFocusOfferRef.current &&
      productId &&
      !isLoading &&
      offerInputRef.current
    ) {
      didFocusOfferRef.current = true;
      offerInputRef.current.focus({ preventScroll: true });
    }
  }, [focusOffer, conversation, isLoading]);

  useEffect(() => {
    didFocusOfferRef.current = false;
  }, [conversationId]);

  useEffect(() => {
    if (conversation && onConversationUpdate) {
      onConversationUpdate(conversation);
    }
  }, [conversation, onConversationUpdate]);

  const contextProduct =
    conversation?.contextProduct ||
    conversation?.product ||
    conversation?.attachedProducts?.[0] ||
    null;
  const productId = contextProduct?.id || contextProduct?._id;
  const listPrice = Number(contextProduct?.price) || 0;

  const refresh = async () => {
    await refetch();
  };

  const handleSendText = async () => {
    const text = chatText.trim();
    if (!text || !conversationId) return;
    setError('');
    try {
      await sendMessage({ conversationId, text, productId });
      setChatText('');
      scrollToBottom('smooth');
    } catch (err) {
      setError(err.message || t('sendFailed', { defaultValue: 'Failed to send' }));
    }
  };

  const handleSubmitOffer = async () => {
    if (!productId) {
      setError(t('selectProductForOffer', { defaultValue: 'Attach a product to make an offer.' }));
      return;
    }
    const amount = parseFloat(bidAmount);
    if (!bidAmount || Number.isNaN(amount) || amount <= 0) {
      setError(t('enterValidAmount', { ns: 'bids' }));
      return;
    }
    setError('');
    try {
      await placeBidAsync({
        productId,
        bidAmount: amount,
        message: chatText.trim() || undefined,
      });
      setBidAmount('');
      setChatText('');
      await refresh();
      scrollToBottom('smooth');
    } catch (err) {
      setError(err.message || t('failedToPlaceBid', { ns: 'bids' }));
    }
  };

  const handleAddToCart = useCallback(
    async (offerId) => {
      if (!offerId || !productId) return;
      try {
        await addBidToCartAsync({ bidId: offerId, productId });
        window.location.href = '/carte';
      } catch (err) {
        setError(err.message || t('failedToAddToCart', { ns: 'bids' }));
      }
    },
    [addBidToCartAsync, productId, t]
  );

  const handleAcceptCounter = async (offerId) => {
    setActionOfferId(offerId);
    setError('');
    try {
      await acceptOfferAsync({ offerId, conversationId });
      await refresh();
    } catch (err) {
      setError(err.message || t('acceptFailed', { defaultValue: 'Failed to accept', ns: 'bids' }));
    } finally {
      setActionOfferId(null);
    }
  };

  const handleRejectCounter = async (offerId) => {
    setActionOfferId(offerId);
    setError('');
    try {
      await rejectOfferAsync({ offerId, conversationId });
      await refresh();
    } catch (err) {
      setError(err.message || t('declineFailed', { defaultValue: 'Failed to decline', ns: 'bids' }));
    } finally {
      setActionOfferId(null);
    }
  };

  if (!conversationId) {
    return (
      <div className="mp-chat-empty">
        <p>{t('selectThread', { defaultValue: 'Select a conversation to start chatting.' })}</p>
      </div>
    );
  }

  if (isLoading && !conversation) {
    return <ChatSkeleton />;
  }

  return (
    <div className="mp-chat">
      <header className="mp-chat__header">
        {onBack && (
          <button type="button" className="mp-chat-back back-button" onClick={onBack} aria-label="Back">
            <ArrowLeft size={22} />
          </button>
        )}
        <div>
          <h2>{storeName}</h2>
          <p>
            {contextProduct
              ? contextProduct.name
              : t('generalConversation', { defaultValue: 'Store conversation' })}
          </p>
        </div>
      </header>

      <div className="mp-chat__messages" ref={messagesContainerRef}>
        {messages.length === 0 && !isFetching ? (
          <p className="mp-chat-empty">{t('startConversation', { defaultValue: 'Say hello or make an offer.' })}</p>
        ) : (
          messages.map((msg, index) => {
            const isUser = isBuyerMessage(msg, buyerId);
            const key = msg.id || index;

            if (isOfferMessage(msg)) {
              return (
                <div
                  key={key}
                  className={`mp-msg-row ${isUser ? 'mp-msg-row--buyer' : 'mp-msg-row--seller'}`}
                >
                  <OfferMessageCard
                    msg={msg}
                    isBuyer={isUser}
                    actionOfferId={actionOfferId}
                    isAddingBidToCart={isAddingBidToCart}
                    onAccept={handleAcceptCounter}
                    onDecline={handleRejectCounter}
                    onAddToCart={handleAddToCart}
                  />
                  <span className="mp-msg-time">{formatMsgTime(msg.createdAt)}</span>
                </div>
              );
            }

            const text = (msg.message || msg.text || '').trim();
            if (!text) return null;

            return (
              <div
                key={key}
                className={`mp-msg-row ${isUser ? 'mp-msg-row--buyer' : 'mp-msg-row--seller'}`}
              >
                <div
                  className={`mp-msg-bubble ${isUser ? 'mp-msg-bubble--buyer' : 'mp-msg-bubble--seller'}`}
                >
                  {text}
                </div>
                <span className="mp-msg-time">{formatMsgTime(msg.createdAt)}</span>
              </div>
            );
          })
        )}
      </div>

      {error && (
        <div className="mp-chat-error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {isOwnThread ? (
        <p className="marketplace-own-store-hint">{t('ownStoreOffer')}</p>
      ) : (
        <ChatComposer
          chatText={chatText}
          onChatTextChange={setChatText}
          bidAmount={bidAmount}
          onBidAmountChange={setBidAmount}
          onSendText={handleSendText}
          onSubmitOffer={handleSubmitOffer}
          isSending={isSending}
          isPlacingBid={isPlacingBid}
          showOfferRow={Boolean(productId) && !hasActiveAccepted}
          listPrice={listPrice}
          offerInputRef={offerInputRef}
          disabled={false}
        />
      )}
    </div>
  );
};

export default MarketplaceChatPane;
